import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { CiFilter, CiExport } from "react-icons/ci";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Autocomplete,
  TextField,
  Popper,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import dayjs from "dayjs";
import ResourceEarningFilter from "./ResourceEarningFilter";
import MonthlyHistory, { formatMonthLabel } from "./MonthlyHistory";
import InProgressProvisional from "./InProgressProvisional";
import ByProjectBreakDown from "./ByProjectBreakDown";
import { useTheme } from "../../../../ThemeContext";
import {
  useFetchResourceEarningsQuery,
  useFetchGetallUserListQuery,
  useExportResourceEarningsMutation,
} from "../../../../slice/logdetailSlice";

const getDatePresetRange = (preset) => {
  let from = "";
  let to = "";
  if (preset === "This month") {
    from = dayjs().startOf("month").format("YYYY-MM-DD");
    to = dayjs().endOf("month").format("YYYY-MM-DD");
  } else if (preset === "Last 3 months") {
    from = dayjs().subtract(2, "month").startOf("month").format("YYYY-MM-DD");
    to = dayjs().endOf("month").format("YYYY-MM-DD");
  } else if (preset === "Last 6 months") {
    from = dayjs().subtract(5, "month").startOf("month").format("YYYY-MM-DD");
    to = dayjs().endOf("month").format("YYYY-MM-DD");
  } else if (preset === "This year") {
    from = dayjs().startOf("year").format("YYYY-MM-DD");
    to = dayjs().endOf("year").format("YYYY-MM-DD");
  } else if (preset === "All time") {
    from = "";
    to = "";
  }
  return { from, to };
};

const ResourceEarningsTab = () => {
  const { isDarkMode } = useTheme();
  const [selectedUserId, setSelectedUserId] = useState("");
  const [datePreset, setDatePreset] = useState("Last 3 months");
  const [fromDate, setFromDate] = useState(() => getDatePresetRange("Last 3 months").from);
  const [toDate, setToDate] = useState(() => getDatePresetRange("Last 3 months").to);
  const [appliedDates, setAppliedDates] = useState(() => ({
    from_date: getDatePresetRange("Last 3 months").from,
    to_date: getDatePresetRange("Last 3 months").to,
  }));

  // Filter Drawer State
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const { data: usersResponse } = useFetchGetallUserListQuery();
  const userList = useMemo(() => {
    const rawList = Array.isArray(usersResponse)
      ? usersResponse
      : Array.isArray(usersResponse?.data)
        ? usersResponse.data
        : [];
    if (rawList.length === 0) return [];

    const userMap = new Map();
    rawList.forEach((u) => {
      const uId = Number(u.user_id || u.id);
      const name = String(u.name || u.user_name || u.full_name || "").trim();
      if (uId && name && !userMap.has(uId)) {
        userMap.set(uId, {
          user_id: uId,
          id: uId,
          name,
        });
      }
    });

    const list = Array.from(userMap.values());
    const currentUser = list.find((u) => u.name.toLowerCase().includes("(you)"));
    const otherUsers = list.filter((u) => u !== currentUser);

    otherUsers.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return currentUser ? [currentUser, ...otherUsers] : otherUsers;
  }, [usersResponse]);

  const {
    data: earningsResponse,
    isLoading: isEarningsLoading,
    isFetching: isEarningsFetching,
  } = useFetchResourceEarningsQuery(
    {
      user_id: selectedUserId,
      from_date: appliedDates.from_date || "",
      to_date: appliedDates.to_date || "",
    },
    {
      skip: !selectedUserId,
    }
  );

  const isFetchingOrLoading = isEarningsFetching || isEarningsLoading;
  const earningsData =
    selectedUserId && !isFetchingOrLoading
      ? earningsResponse?.data || earningsResponse
      : null;
  const summary = useMemo(() => earningsData?.summary || {}, [earningsData]);
  const monthlyHistory = useMemo(() => earningsData?.monthly_history || [], [earningsData]);
  const inProgress = useMemo(() => earningsData?.in_progress || [], [earningsData]);
  const byProject = useMemo(() => earningsData?.by_project || [], [earningsData]);

  const handleClearResource = () => {
    setSelectedUserId("");
    setDatePreset("Last 3 months");
    const { from, to } = getDatePresetRange("Last 3 months");
    setFromDate(from);
    setToDate(to);
    setAppliedDates({ from_date: from, to_date: to });
  };

  const handleTopDatePresetChange = (e) => {
    const preset = e.target.value;
    if (preset === "Custom date" || preset === "Custom range") {
      setShowFilterDrawer(true);
      return;
    }
    setDatePreset(preset);
    const { from, to } = getDatePresetRange(preset);
    setFromDate(from);
    setToDate(to);
    setAppliedDates({
      from_date: from,
      to_date: to,
    });
  };

  const handleApplyFilter = ({ userId, datePreset: newPreset, fromDate: newFrom, toDate: newTo }) => {
    if (!userId) {
      handleClearResource();
      setShowFilterDrawer(false);
      return;
    }
    setSelectedUserId(userId);
    setDatePreset(newPreset);
    setFromDate(newFrom);
    setToDate(newTo);
    setAppliedDates({
      from_date: newFrom,
      to_date: newTo,
    });
    setShowFilterDrawer(false);
    toast.success("Filters applied successfully");
  };

  const monthlyRangeText = useMemo(() => {
    if (appliedDates.from_date && appliedDates.to_date) {
      return `${dayjs(appliedDates.from_date).format("DD MMM YYYY")} – ${dayjs(appliedDates.to_date).format("DD MMM YYYY")}`;
    }
    if (monthlyHistory.length > 0) {
      const first = formatMonthLabel(monthlyHistory[0]?.month);
      const last = formatMonthLabel(monthlyHistory[monthlyHistory.length - 1]?.month);
      return first === last ? first : `${first} – ${last}`;
    }
    return "All time";
  }, [appliedDates, monthlyHistory]);

  const selectedUserObj = useMemo(() => {
    if (!selectedUserId) return null;
    return (
      userList.find(
        (u) => Number(u.user_id || u.id) === Number(selectedUserId)
      ) || null
    );
  }, [selectedUserId, userList]);

  const hasData = Boolean(
    selectedUserId &&
      ((Array.isArray(monthlyHistory) && monthlyHistory.length > 0) ||
        (Array.isArray(byProject) && byProject.length > 0) ||
        (Array.isArray(inProgress) && inProgress.length > 0))
  );

  const [exportResourceEarnings, { isLoading: isExporting }] =
    useExportResourceEarningsMutation();

  const handleExportClick = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (hasData === false) {
      toast.error("No data available to export");
      return;
    }

    // Close any existing popup first
    Swal.close();

    const result = await Swal.fire({
      title: "Export Confirmation",
      text: "Are you sure you want to export?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Export",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#04a189",
      cancelButtonColor: "#d33",
      allowOutsideClick: true,
      allowEscapeKey: true,
      backdrop: true,
      customClass: {
        popup: "custom-swal-popup",
        title: "custom-swal-title",
        htmlContainer: "custom-swal-html",
        icon: "custom-swal-icon",
        confirmButton: "swal-confirm-btn",
        cancelButton: "swal-cancel-btn",
      },
    });

    if (result.isConfirmed) {
      await executeExport();
    }
  };

  const executeExport = async () => {
    try {
      const params = {
        user_id: selectedUserId || "",
        from_date: appliedDates?.from_date || "",
        to_date: appliedDates?.to_date || "",
      };

      const response = await exportResourceEarnings(params).unwrap();

      if (response?.status && response?.doc_url) {
        toast.success(
          response?.message || "Resource earnings exported successfully."
        );
        const link = document.createElement("a");
        link.href = response.doc_url;
        link.setAttribute("download", "");
        link.setAttribute("target", "_blank");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (response?.doc_url) {
        toast.success(
          response?.message || "Resource earnings exported successfully."
        );
        window.open(response.doc_url, "_blank");
      } else {
        toast.error(
          response?.message || "Export failed, no download link returned."
        );
      }
    } catch (error) {
      console.error("Resource earnings export error:", error);
      toast.error(
        error?.data?.message ||
          error?.message ||
          "Failed to export resource earnings."
      );
    }
  };

  const isFiltered = Boolean(selectedUserId);

  return (
    <div className="container-fluid p-0">
      {/* 1. Top Header Action Bar with Border Box matching InvoiceQueueTab */}
      <Box
        className="d-flex flex-wrap align-items-center justify-content-between gap-3"
        sx={{
          border: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
          borderRadius: "6px",
          padding: "10px 16px",
          marginBottom: "16px",
          backgroundColor: isDarkMode ? "transparent" : "#fff",
        }}
      >
        {/* Left: Resource Dropdown directly in border box */}
        <div className="d-flex align-items-center flex-wrap gap-2">
          <Autocomplete
            id="resource-select-filter"
            size="small"
            options={userList}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) =>
              Number(option?.user_id || option?.id) === Number(value?.user_id || value?.id)
            }
            value={selectedUserObj}
            onChange={(event, newValue) => {
              if (!newValue) {
                handleClearResource();
              } else {
                setSelectedUserId(newValue.user_id || newValue.id);
              }
            }}
            filterOptions={(options, { inputValue }) => {
              const query = (inputValue || "").toLowerCase().trim();
              if (!query) return options;
              return options.filter((opt) =>
                (opt?.name || "").toLowerCase().includes(query)
              );
            }}
            PopperComponent={(popperProps) => (
              <Popper
                {...popperProps}
                placement="bottom-start"
                modifiers={[
                  {
                    name: "flip",
                    enabled: true,
                    options: {
                      fallbackPlacements: ["top-start", "bottom-start"],
                      padding: 8,
                    },
                  },
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      boundary: "viewport",
                      padding: 8,
                    },
                  },
                ]}
                sx={{ zIndex: 1700 }}
              />
            )}
            componentsProps={{
              paper: {
                sx: {
                  backgroundColor: isDarkMode ? "#1e293b !important" : "#ffffff !important",
                  color: isDarkMode ? "#f2f1f1 !important" : "#1f2937 !important",
                  border: isDarkMode ? "1px solid #475569 !important" : "1px solid #e5e7eb !important",
                  "& .MuiAutocomplete-noOptions": {
                    color: isDarkMode ? "#f2f1f1 !important" : "#6b7280 !important",
                    fontSize: "14px",
                    padding: "10px 14px",
                  },
                },
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: "216px !important",
                overflowY: "auto",
                backgroundColor: isDarkMode ? "#1e293b !important" : "#ffffff !important",
                color: isDarkMode ? "#f2f1f1 !important" : "#1f2937 !important",
              },
            }}
            renderInput={(inputParams) => (
              <TextField
                {...inputParams}
                placeholder="Select & search resource..."
                size="small"
                onKeyDown={(e) => e.stopPropagation()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                    height: "36px !important",
                    minHeight: "36px !important",
                    boxSizing: "border-box",
                    padding: "0 8px !important",
                    backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                    color: isDarkMode ? "#f2f1f1" : "#1e293b",
                    "& fieldset": {
                      borderColor: isDarkMode ? "#475569" : "#cbd5e1",
                    },
                    "&:hover fieldset": {
                      borderColor: isDarkMode ? "#94a3b8" : "#94a3b8",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "4px 4px !important",
                    fontSize: "14px",
                    color: isDarkMode ? "#f2f1f1" : "#1e293b",
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                    visibility: selectedUserObj ? "visible" : "hidden",
                    padding: "2px",
                  },
                  "& .MuiAutocomplete-popupIndicator": {
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                    padding: "2px",
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option.user_id || option.id}
                style={{
                  fontSize: "14px",
                  minHeight: "36px",
                  padding: "6px 12px",
                  fontFamily: "inherit",
                }}
              >
                {option.name}
              </li>
            )}
            noOptionsText={
              <span className={isDarkMode ? "text-white" : "text-secondary"}>
                No resources found
              </span>
            }
            sx={{
              width: 250,
              minWidth: 250,
              height: "36px",
              my: "auto",
              "& .MuiFormControl-root": {
                height: "36px",
                my: "auto",
              },
            }}
          />

          {/* Date Range Preset Dropdown - only shown when a resource is selected */}
          {Boolean(selectedUserId) && (
            <>
              <FormControl size="small" sx={{ width: 165 }}>
                <Select
                  value={datePreset}
                  onChange={handleTopDatePresetChange}
                  size="small"
                  IconComponent={ExpandMore}
                  sx={{
                    height: "36px !important",
                    minHeight: "36px !important",
                    fontSize: "14px",
                    backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                    color: isDarkMode ? "#f2f1f1" : "#1e293b",
                    "& fieldset": {
                      borderColor: isDarkMode ? "#475569" : "#cbd5e1",
                    },
                    "&:hover fieldset": {
                      borderColor: isDarkMode ? "#94a3b8" : "#94a3b8",
                    },
                    "& .MuiSelect-select": {
                      padding: "6px 12px !important",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-icon": {
                      color: isDarkMode ? "#9ca3af" : "#6b7280",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 280,
                        mt: 0.5,
                        backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                        color: isDarkMode ? "#f2f1f1" : "#1e293b",
                        border: isDarkMode ? "1px solid #475569" : "1px solid #e5e7eb",
                        "& .MuiMenuItem-root": {
                          fontSize: "14px",
                          padding: "8px 14px",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="All time">All time</MenuItem>
                  <MenuItem value="This month">This month</MenuItem>
                  <MenuItem value="Last 3 months">Last 3 months</MenuItem>
                  <MenuItem value="Last 6 months">Last 6 months</MenuItem>
                  <MenuItem value="This year">This year</MenuItem>
                  <MenuItem value="Custom date">Custom date</MenuItem>
                </Select>
              </FormControl>

              {/* Custom Date Range Badge (if Custom date chosen via drawer) */}
              {(datePreset === "Custom date" || datePreset === "Custom range") && appliedDates.from_date && appliedDates.to_date && (
                <span
                  className="d-inline-flex align-items-center px-3 py-1 rounded-pill"
                  style={{
                    backgroundColor: isDarkMode ? "#333" : "#f1f5f9",
                    color: isDarkMode ? "#cbd5e1" : "#475569",
                    fontSize: "13px",
                    fontWeight: 500,
                  }}
                >
                  <span className="fw-semibold me-1">Custom:</span>{" "}
                  {`${dayjs(appliedDates.from_date).format("DD-MM-YYYY")} – ${dayjs(appliedDates.to_date).format("DD-MM-YYYY")}`}
                </span>
              )}
            </>
          )}
        </div>

        {/* Right: Clear Filter (if active), Filter Button, Export Button */}
        <div className="d-flex align-items-center gap-2 ms-auto">
          {isFiltered && (
            <button
              type="button"
              className="addmore_btn"
              onClick={handleClearResource}
            >
              Clear Filter
            </button>
          )}

          <Tooltip title="Filter">
            <IconButton
              onClick={() => setShowFilterDrawer(true)}
              className="me-1"
              sx={{
                backgroundColor: isDarkMode
                  ? "rgba(67, 105, 255, 0.08)"
                  : "#F1F5FF",
                borderRadius: "50%",
                width: "39px",
                height: "39px",
                position: "relative",
                "&:hover": {
                  backgroundColor: "var(--icon-hover-bg)",
                },
              }}
            >
              <CiFilter className="text-rgbr-blue pro-icon-s" />
              {isFiltered && (
                <span
                  style={{
                    position: "absolute",
                    top: "7px",
                    right: "7px",
                    width: "8px",
                    height: "8px",
                    backgroundColor: "rgba(18, 183, 159, 1)",
                    borderRadius: "50%",
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title={isExporting ? "Exporting..." : "Export"}>
            <span>
              <IconButton
                onClick={handleExportClick}
                disabled={isExporting}
                sx={{
                  backgroundColor: isDarkMode
                    ? "rgba(67, 105, 255, 0.08)"
                    : "#F1F5FF",
                  borderRadius: "50%",
                  width: "39px",
                  height: "39px",
                  "&:hover": {
                    backgroundColor: "var(--icon-hover-bg)",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                {isExporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CiExport className="text-rgbr-blue pro-icon-s" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </Box>

      {/* Filter Sidebar Drawer (Extracted Component) */}
      <ResourceEarningFilter
        show={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        userList={userList}
        selectedUserId={selectedUserId}
        datePreset={datePreset}
        fromDate={fromDate}
        toDate={toDate}
        onApply={handleApplyFilter}
        onClear={handleClearResource}
        isEarningsFetching={isEarningsFetching}
      />

      {/* 2. 5 KPI Cards (Bootstrap Grid matching Image 1) */}
      <div className="row g-3 mb-4">
        {/* 1. LOADED COST (RANGE) */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                minHeight: "28px",
              }}
            >
              LOADED COST (RANGE)
            </div>
            <div className={`fs-4 fw-bold mt-2 ${isDarkMode ? "text-white" : "text-dark"}`}>
              ₹{selectedUserId
                ? Number(summary?.loaded_cost || 0).toLocaleString("en-IN")
                : "0"}
            </div>
          </div>
        </div>

        {/* 2. REALISED EARNING */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                minHeight: "28px",
              }}
            >
              REALISED EARNING
            </div>
            <div className={`fs-4 fw-bold mt-2 ${isDarkMode ? "text-white" : "text-dark"}`}>
              ₹{selectedUserId
                ? Number(summary?.realised_earning || 0).toLocaleString("en-IN")
                : "0"}
            </div>
          </div>
        </div>

        {/* 3. REALISED RATIO */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                minHeight: "28px",
              }}
            >
              REALISED RATIO
            </div>
            <div
              className="fs-4 fw-bold mt-2"
              style={{
                color:
                  selectedUserId && summary?.ratio != null
                    ? Number(summary.ratio) >= 1
                      ? isDarkMode ? "#4ade80" : "#16a34a"
                      : Number(summary.ratio) > 0
                      ? isDarkMode ? "#fbbf24" : "#d97706"
                      : isDarkMode ? "#94a3b8" : "#64748b"
                    : isDarkMode ? "#94a3b8" : "#64748b",
              }}
            >
              {selectedUserId && summary?.ratio != null
                ? `${Number(summary.ratio).toFixed(2)}x`
                : "—"}
            </div>
          </div>
        </div>

        {/* 4. PROVISIONAL — NOT ALLOCATED */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                minHeight: "28px",
                lineHeight: "1.3",
              }}
            >
              PROVISIONAL — NOT
              <br />
              ALLOCATED
            </div>
            <div className={`fs-4 fw-bold mt-2 ${isDarkMode ? "text-white" : "text-dark"}`}>
              ₹{selectedUserId
                ? Number(summary?.provisional_earning || 0).toLocaleString("en-IN")
                : "0"}
            </div>
          </div>
        </div>

        {/* 5. UTILISATION */}
        <div className="col-12 col-sm-6 col-lg">
          <div className={`card h-100 border rounded shadow-sm p-3 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
            <div
              className="text-muted small fw-semibold text-uppercase"
              style={{
                fontSize: "11px",
                letterSpacing: "0.5px",
                minHeight: "28px",
              }}
            >
              UTILISATION
            </div>
            <div
              className="fs-4 fw-bold mt-2"
              style={{
                color:
                  selectedUserId && summary?.utilisation != null
                    ? Number(summary.utilisation) < 20
                      ? isDarkMode ? "#f87171" : "#dc2626"
                      : Number(summary.utilisation) < 50
                      ? isDarkMode ? "#fbbf24" : "#d97706"
                      : isDarkMode ? "#4ade80" : "#16a34a"
                    : isDarkMode ? "#94a3b8" : "#64748b",
              }}
            >
              {selectedUserId && summary?.utilisation != null
                ? `${Number(summary.utilisation) % 1 === 0 ? summary.utilisation : Number(summary.utilisation).toFixed(2)}%`
                : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* 3. Monthly History Section (Extracted Component) */}
      <MonthlyHistory
        selectedUserId={selectedUserId}
        monthlyHistory={monthlyHistory}
        summary={summary}
        monthlyRangeText={monthlyRangeText}
        isLoading={isFetchingOrLoading}
        isDarkMode={isDarkMode}
      />

      {/* 4. In Progress Provisional Section */}
      <InProgressProvisional
        selectedUserId={selectedUserId}
        inProgress={inProgress}
        isLoading={isFetchingOrLoading}
        isDarkMode={isDarkMode}
      />

      {/* 5. By Project Breakdown Section (Extracted Component) */}
      <ByProjectBreakDown
        selectedUserId={selectedUserId}
        inProgress={inProgress}
        byProject={byProject}
        isLoading={isFetchingOrLoading}
        isDarkMode={isDarkMode}
      />

      {/* 5. Informational Alert Banner */}
      <div className="alert alert-info d-flex align-items-center gap-2 border-0 rounded p-3 mb-0" style={{ backgroundColor: "#eff6ff", color: "#1e40af" }}>
        <div>
          <strong>PM Supervision Note:</strong> A five-month ratio of 0.70x on a Project Manager is not necessarily a problem — supervision time is real work that no client pays for directly. It is a signal to verify whether PM overhead is priced into client quotations properly.
        </div>
      </div>
    </div>
  );
};

export default ResourceEarningsTab;
